<template>
    <v-card class="pa-2 app-screen-bg options-page">
        <div class="options-page__scroll">
            <div class="options-title-pill-wrap">
                <span class="options-title-pill">{{ $t('user.options.titlePill') }}</span>
            </div>
            <v-card class="options-content-card" :class="$vuetify.theme.dark ? 'transparent03--dark white--text' : 'transparent03 black--text'">
                <div class="options-tabs">
                    <div
                        v-for="mark in bookmarks"
                        :key="mark"
                        class="options-tab btn-shadow"
                        :class="{'accent': bookmark === mark}"
                        @click="goToTab(mark)"
                    >
                        <span class="options-tab-label">{{ tabLabels[mark] }}</span>
                    </div>
                </div>

                <Swiper
                    ref="tabSwiper"
                    class="options-swiper"
                    :options="tabSwiperOption"
                    :style="{height: tabAreaHeight + 'px'}"
                    @slideChange="onTabSlideChange"
                >
                    <swiper-slide v-for="mark in bookmarks" :key="mark">
                        <v-card-text v-if="mark === 'SoundNotifications'" class="options-pane">
                            <h3 class="options-section-title">{{ $t('user.options.soundSection') }}</h3>
                            <SoundSettings/>

                            <v-divider class="my-6"/>

                            <h3 class="options-section-title">{{ $t('user.options.notificationsSection') }}</h3>
                            <p class="options-hint mb-2">
                                {{ $t('user.notificationSettings.hint') }}
                            </p>
                            <v-row dense>
                                <v-col cols="6">
                                    <v-switch
                                        :input-value="notifyExpFlag"
                                        @change="setNotifyFlag('notifyExp', $event)"
                                        hide-details
                                        color="secondary"
                                        :label="$t('user.notificationSettings.categoryExp')"
                                    />
                                    <v-switch
                                        :input-value="notifyBadgeFlag"
                                        @change="setNotifyFlag('notifyBadge', $event)"
                                        hide-details
                                        color="secondary"
                                        class="mt-2"
                                        :label="$t('user.notificationSettings.categoryBadge')"
                                    />
                                    <v-switch
                                        :input-value="notifyPokemonFlag"
                                        @change="setNotifyFlag('notifyPokemon', $event)"
                                        hide-details
                                        color="secondary"
                                        class="mt-2"
                                        :label="$t('user.notificationSettings.categoryPokemon')"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <v-switch
                                        :input-value="notifyOtherFlag"
                                        @change="setNotifyFlag('notifyOther', $event)"
                                        hide-details
                                        color="secondary"
                                        :label="$t('user.notificationSettings.categoryOther')"
                                    />
                                    <v-switch
                                        :input-value="notifyErrFlag"
                                        @change="setNotifyFlag('notifyErr', $event)"
                                        hide-details
                                        color="secondary"
                                        class="mt-2"
                                        :label="$t('user.notificationSettings.categoryErr')"
                                    />
                                </v-col>
                            </v-row>
                        </v-card-text>

                        <v-card-text v-if="mark === 'AppearanceBehavior'" class="options-pane">
                            <h3 class="options-section-title">{{ $t('user.options.appearanceSection') }}</h3>
                            <v-switch
                                :input-value="darkThemeFlag"
                                @change="setDarkThemeFlag"
                                hide-details
                                color="secondary"
                                :label="$t('user.options.darkThemeLabel')"
                            />
                            <v-select
                                :value="skinFlag"
                                @change="setSkinFlag"
                                :items="availableSkins"
                                hide-details
                                color="secondary"
                                :label="$t('user.options.skinLabel')"
                                class="mt-4"
                                style="max-width: 300px;"
                            />
                            <v-select
                                :value="languageFlag"
                                @change="setLanguageFlag"
                                :items="availableLanguages"
                                hide-details
                                color="secondary"
                                :label="$t('dashboard.options.languageLabel')"
                                class="mt-4"
                                style="max-width: 300px;"
                            />

                            <v-divider class="my-6"/>

                            <h3 class="options-section-title">{{ $t('user.options.behaviorSection') }}</h3>
                            <v-switch
                                :input-value="autoConfirmFlag"
                                @change="setAutoConfirmFlag"
                                hide-details
                                color="secondary"
                                :label="$t('user.options.autoConfirmLabel')"
                            />
                            <p class="options-hint mt-1">
                                {{ $t('user.options.autoConfirmHint') }}
                            </p>

                            <v-switch
                                :input-value="rightHandedFlag"
                                @change="setRightHandedFlag"
                                hide-details
                                color="secondary"
                                :label="$t('user.options.rightHandedLabel')"
                                class="mt-4"
                            />
                        </v-card-text>

                        <v-card-text v-if="mark === 'Data'" class="options-pane">
                            <h3 class="options-section-title">{{ $t('user.options.dataSection') }}</h3>
                            <p class="options-hint">
                                {{ $t('user.options.dataHint') }}
                            </p>
                            <v-switch
                                :input-value="prefetchImagesFlag"
                                @change="setPrefetchImages"
                                hide-details
                                color="secondary"
                                :label="$t('user.options.prefetchImagesLabel')"
                            />
                            <div v-if="prefetchingImages" class="options-hint">{{ $t('user.options.prefetchImagesProgress', {progress: imagesProgress}) }}</div>
                            <v-switch
                                :input-value="prefetchMusicFlag"
                                @change="setPrefetchMusic"
                                hide-details
                                color="secondary"
                                class="mt-2"
                                :label="$t('user.options.prefetchMusicLabel')"
                            />
                            <div v-if="prefetchingMusic" class="options-hint">{{ $t('user.options.prefetchMusicProgress', {progress: musicProgress}) }}</div>

                            <template v-if="!pwaInstalled">
                                <v-divider class="my-6"/>

                                <h3 class="options-section-title">{{ $t('user.options.pwaSectionTitle') }}</h3>
                                <v-btn v-if="pwaPromptAvailable" color="secondary" @click="installPwa">
                                    <v-icon left>mdi-download</v-icon>
                                    {{ $t('user.options.pwaInstallButton') }}
                                </v-btn>
                                <p v-else-if="pwaIsIOS" class="options-hint">
                                    {{ $t('user.options.pwaIosHint') }}
                                </p>
                                <p v-else class="options-hint">
                                    {{ $t('user.options.pwaGenericHint') }}
                                </p>
                            </template>
                        </v-card-text>
                    </swiper-slide>
                </Swiper>
            </v-card>
        </div>
    </v-card>
</template>

<script>
    import {Swiper, SwiperSlide} from 'vue-awesome-swiper';
    import {flags, setFlag, skinSelectItems, languageSelectItems} from '@/config/featureFlags';
    import {prefetchImageAssets, prefetchMusicAssets, isRunningAsInstalledApp} from '@/utils/offlineAssets';
    import {isInstallPromptAvailable, isIOS, promptInstall} from '@/utils/pwaInstall';
    import SoundSettings from '@/components/user/SoundSettings';

    export default {
        components: {SoundSettings, Swiper, SwiperSlide},
        data() {
            return {
                prefetchingImages: false,
                prefetchingMusic: false,
                imagesProgress: '',
                musicProgress: '',
                bookmark: 'SoundNotifications',
                bookmarks: ['SoundNotifications', 'AppearanceBehavior', 'Data'],
                tabAreaHeight: 400,
                pwaInstalled: isRunningAsInstalledApp(),
                pwaPromptAvailable: isInstallPromptAvailable(),
                pwaIsIOS: isIOS(),
            }
        },
        computed: {
            tabLabels() {
                return {
                    'SoundNotifications': this.$t('user.options.tabs.soundNotifications'),
                    'AppearanceBehavior': this.$t('user.options.tabs.appearanceBehavior'),
                    'Data': this.$t('user.options.tabs.data'),
                };
            },
            tabSwiperOption() {
                return {
                    loop: true,
                    speed: 300,
                    noSwipingClass: 'swiper-no-swiping',
                    initialSlide: Math.max(0, this.bookmarks.indexOf(this.bookmark)),
                };
            },
            darkThemeFlag() {
                return flags.darkTheme;
            },
            skinFlag() {
                return flags.skin;
            },
            availableSkins() {
                return skinSelectItems();
            },
            languageFlag() {
                return flags.language;
            },
            availableLanguages() {
                return languageSelectItems();
            },
            prefetchImagesFlag() {
                return flags.prefetchImages;
            },
            prefetchMusicFlag() {
                return flags.prefetchMusic;
            },
            autoConfirmFlag() {
                return flags.autoConfirm;
            },
            rightHandedFlag() {
                return flags.rightHanded;
            },
            notifyExpFlag() {
                return flags.notifyExp;
            },
            notifyBadgeFlag() {
                return flags.notifyBadge;
            },
            notifyPokemonFlag() {
                return flags.notifyPokemon;
            },
            notifyOtherFlag() {
                return flags.notifyOther;
            },
            notifyErrFlag() {
                return flags.notifyErr;
            },
        },
        watch: {
            // progress texty (Data tab) a PWA sekce meni vysku obsahu - swiper potrebuje prepocitat, jinak zustane oriznuty/s mezerou
            prefetchingImages() {
                this.$nextTick(this.updateTabAreaHeight);
            },
            prefetchingMusic() {
                this.$nextTick(this.updateTabAreaHeight);
            },
            pwaInstalled() {
                this.$nextTick(this.updateTabAreaHeight);
            },
        },
        mounted() {
            this.$nextTick(this.updateTabAreaHeight);
            window.addEventListener('resize', this.updateTabAreaHeight);
            // reaguje i na beforeinstallprompt/appinstalled vyvolane AZ po namountovani teto stranky (pwaInstall.js sam jen tise zachytava event do promenne)
            window.addEventListener('beforeinstallprompt', this.onPwaStateChange);
            window.addEventListener('appinstalled', this.onPwaStateChange);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.updateTabAreaHeight);
            window.removeEventListener('beforeinstallprompt', this.onPwaStateChange);
            window.removeEventListener('appinstalled', this.onPwaStateChange);
        },
        methods: {
            setDarkThemeFlag(v) {
                setFlag('darkTheme', !!v);
            },
            setSkinFlag(v) {
                setFlag('skin', v);
            },
            setLanguageFlag(v) {
                setFlag('language', v);
            },
            setAutoConfirmFlag(v) {
                setFlag('autoConfirm', !!v);
            },
            setRightHandedFlag(v) {
                setFlag('rightHanded', !!v);
            },
            setNotifyFlag(name, value) {
                setFlag(name, !!value);
            },
            setPrefetchImages(v) {
                setFlag('prefetchImages', !!v);
                if (v) {
                    this.prefetchingImages = true;
                    prefetchImageAssets((done, total) => { this.imagesProgress = done+'/'+total; })
                        .finally(() => { this.prefetchingImages = false; });
                }
            },
            setPrefetchMusic(v) {
                setFlag('prefetchMusic', !!v);
                if (v) {
                    this.prefetchingMusic = true;
                    prefetchMusicAssets((done, total) => { this.musicProgress = done+'/'+total; })
                        .finally(() => { this.prefetchingMusic = false; });
                }
            },
            installPwa() {
                promptInstall().then(() => {
                    this.onPwaStateChange();
                });
            },
            onPwaStateChange() {
                this.pwaInstalled = isRunningAsInstalledApp();
                this.pwaPromptAvailable = isInstallPromptAvailable();
            },
            updateTabAreaHeight() {
                const swiperEl = this.$refs.tabSwiper && this.$refs.tabSwiper.$el;
                const scrollContainer = swiperEl && swiperEl.closest('.options-page__scroll');
                if (!swiperEl || !scrollContainer) {
                    return;
                }
                const top = swiperEl.getBoundingClientRect().top;
                const visibleBottom = scrollContainer.getBoundingClientRect().bottom;
                const next = Math.max(320, Math.floor(visibleBottom - top - 4));
                if (next === this.tabAreaHeight) {
                    return;
                }
                this.tabAreaHeight = next;
                this.$nextTick(() => {
                    const swiper = this.$refs.tabSwiper && this.$refs.tabSwiper.$swiper;
                    if (swiper) {
                        swiper.update();
                    }
                });
            },
            goToTab(mark) {
                if (this.bookmark === mark) {
                    return;
                }
                const swiper = this.$refs.tabSwiper && this.$refs.tabSwiper.$swiper;
                if (swiper) {
                    swiper.slideToLoop(this.bookmarks.indexOf(mark));
                } else {
                    this.bookmark = mark;
                }
            },
            onTabSlideChange() {
                const swiper = this.$refs.tabSwiper && this.$refs.tabSwiper.$swiper;
                if (!swiper) {
                    return;
                }
                const mark = this.bookmarks[swiper.realIndex];
                if (mark !== undefined) {
                    this.bookmark = mark;
                }
            },
        },
    }
</script>

<style scoped>
    /* height:100% misto 100vh - viz ".new-shell-ui .v-main" v App.vue, stejny vzor jako News.vue/GameRules.vue */
    .options-page {
        height: 100%;
        display: flex;
        flex-direction: column;
    }
    .options-page__scroll {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
    }
    .options-title-pill-wrap {
        position: sticky;
        top: 0;
        z-index: 3;
        display: flex;
        justify-content: center;
        padding: 8px 0;
        background: transparent;
    }
    .options-title-pill {
        display: inline-block;
        background: rgba(0, 0, 0, .7);
        color: #fff;
        font-size: 20px;
        font-weight: 700;
        padding: 6px 20px;
        border-radius: 20px;
        text-shadow: 1px 1px 2px rgba(0, 0, 0, .6);
    }
    .options-content-card {
        max-width: 700px;
        margin: 0 auto;
    }
    .options-section-title {
        margin-bottom: 8px;
    }
    .options-hint {
        font-size: 14px;
        opacity: 0.75;
    }

    /* zalozky (klik na nadpis + swipe) - stejny vzor jako .trainer-bag__tab* v TrainerBag.vue */
    .options-tabs {
        container-type: inline-size;
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        padding: 10px 12px 6px;
    }
    .options-tab {
        flex: 1 1 84px;
        min-width: 84px;
        text-align: center;
        padding: 6px 4px;
        border-radius: 6px;
        background-color: rgba(0, 0, 0, .06);
        border: 1px solid rgba(0, 0, 0, .1);
        cursor: pointer;
        font-weight: 600;
        font-size: clamp(11px, 3.1cqi, 14px);
    }
    .options-tab-label {
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    /* neaktivni slide bez vlastniho overflow:hidden muze pretekat do sousedniho tabu (viz claude/components/trainer.md, stejny problem jako TrainerBag.vue) - plain descendant selector, ne >>>, protoze <swiper-slide> je komponenta primo v tomto templatu a jeji root element tak nese parent scope atribut */
    .options-swiper .swiper-slide {
        overflow: hidden;
        box-sizing: border-box;
        height: 100%;
    }
    .options-pane {
        height: 100%;
        overflow-y: auto;
        box-sizing: border-box;
    }
</style>
